import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sanitizeString } from '../utils/security';
import {
  fetchCustomerDetail,
  updateCustomer,
  fetchMusteriIletisimIzniGecmisi,
} from '../services/customersService';
import {
  fetchDevicesByCustomer,
  createDevice,
  deleteDevice,
} from '../services/devicesService';
import DeviceProductTreePicker from '../components/DeviceProductTreePicker';
import {
  fetchCities,
  fetchDistrictsByCity,
} from '../services/locationsService';
import {
  fetchAddressesByCustomer,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
} from '../services/addressesService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { getCustomerSms } from '../services/smsService';
import TableListFooter from '../components/TableListFooter';
import SmsDurumIkonlari from '../components/SmsDurumIkonlari';
import SmsNetgsmDetayModal from '../components/SmsNetgsmDetayModal';
import MusteriCagriListesi from '../components/MusteriCagriListesi';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama, clampPage } from '../utils/pagination';
import './MusteriHizmetleriPage.css';

const CUSTOMER_TABS = [
  { key: 'info', label: 'Müşteri Bilgileri' },
  { key: 'addresses', label: 'Adresler' },
  { key: 'devices', label: 'Cihaz Listesi' },
  { key: 'sms', label: 'SMS' },
  { key: 'calls', label: 'Çağrılar' },
  { key: 'kvkk', label: 'KVKK' },
];

const getKurulumRandevuHref = (device) => {
  const rv = device?.kurulumRandevu;
  if (rv?.id && rv?.servisKaydiId) {
    return `/service-records/${rv.servisKaydiId}/appointments/${rv.id}`;
  }
  return null;
};

const formatDeviceDateTr = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('tr-TR');
  } catch {
    return null;
  }
};

const DeviceEskiKayitBadge = () => (
  <span className="eski-sk-row-badge">Eski</span>
);

const DeviceEskiServisKaydiCard = ({ device }) => {
  const esk = device?.eskiServisKaydi || {};
  const kodu = esk.kodu ? String(esk.kodu).trim() : '';
  const tekniker = esk.tekniker ? String(esk.tekniker).trim() : '';
  const bayi = esk.bayi ? String(esk.bayi).trim() : '';
  const musteriKodu = esk.musteriKodu ? String(esk.musteriKodu).trim() : '';
  const ilkCalistirma = formatDeviceDateTr(device?.ilkCalistirmaTarihi || device?.garantiBaslangicTarihi);
  const garantiBitis = formatDeviceDateTr(device?.garantiBitisTarihi);
  const garantiSure = Number.isFinite(Number(device?.garantiSuresi)) && Number(device.garantiSuresi) > 0
    ? `${Number(device.garantiSuresi)} ay`
    : '';

  const alanlar = [
    { label: 'Teknisyen', value: tekniker },
    { label: 'Bayi', value: bayi },
    { label: 'Müşteri kodu', value: musteriKodu },
    { label: 'İlk çalıştırma', value: ilkCalistirma },
    { label: 'Garanti', value: [garantiSure, garantiBitis].filter(Boolean).join(' · ') },
  ].filter((a) => a.value);

  return (
    <div className="eski-sk-card">
      <div className="eski-sk-card-head">
        <div className="eski-sk-card-title">
          <i className="fas fa-archive" aria-hidden="true" />
          Eski servis kaydı
          {kodu ? <span className="eski-sk-card-kod">{kodu}</span> : null}
        </div>
      </div>
      {alanlar.length > 0 ? (
        <div className="eski-sk-grid">
          {alanlar.map((alan) => (
            <div key={alan.label} className="eski-sk-field">
              <span className="eski-sk-field-label">{alan.label}</span>
              <span className="eski-sk-field-value">{alan.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const DeviceKurulumRandevuLink = ({ device }) => {
  const href = getKurulumRandevuHref(device);
  if (href) {
    const randevuNo = device?.kurulumRandevu?.randevuNumarasi;
    return (
      <div style={{ marginBottom: '10px' }}>
        <Link
          to={href}
          style={{
            color: 'var(--primary, #2563eb)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '13px',
          }}
          title={randevuNo ? `Randevu: ${randevuNo}` : 'Randevu detayı'}
        >
          İlgili randevu{randevuNo ? ` (${randevuNo})` : ''}
        </Link>
      </div>
    );
  }

  if (!device?.eskiKayit && !device?.eskiServisKaydi) return null;
  return <DeviceEskiServisKaydiCard device={device} />;
};

const formatSmsDate = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '—';
  }
};

const getSmsTipBadge = (tip) => {
  const tipMap = {
    servis_kaydi_acildi: { text: 'Servis Kaydı Açıldı', color: '#3b82f6', bg: '#dbeafe' },
    randevu_olusturuldu: { text: 'Randevu Oluşturuldu', color: '#10b981', bg: '#d1fae5' },
    randevu_iptal_edildi: { text: 'Randevu İptal Edildi', color: '#ef4444', bg: '#fee2e2' },
    otp: { text: 'OTP Doğrulama', color: '#8b5cf6', bg: '#ede9fe' },
    servis_kaydi_randevu_detay: { text: 'Servis Detay Raporu', color: '#0d9488', bg: '#ccfbf1' },
    musteri_iletisim_izni_otp: { text: 'İletişim İzni OTP', color: '#6366f1', bg: '#eef2ff' },
  };
  const tipInfo = tipMap[tip] || { text: tip, color: '#64748b', bg: '#f1f5f9' };
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: tipInfo.bg,
        color: tipInfo.color,
      }}
    >
      {tipInfo.text}
    </span>
  );
};

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) {
    return fallback;
  }

  if (error?.status === 404) {
    return 'API endpoint bulunamadı.';
  }

  if (error?.status === 401) {
    return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  }
  
  if (error?.status === 403) {
    return 'Bu işlem için yetkiniz yok.';
  }

  const hatalar = error?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    const hataMesajlari = hatalar
      .map(h => h.mesaj || h.message)
      .filter(Boolean);
    
    if (hataMesajlari.length > 0) {
      return hataMesajlari.join('. ');
    }
  }

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (error?.message === 'Failed to fetch' || message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

/** API'den gelen il/ilçe alanından select value için ID çıkar */
const resolveLocationId = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'object') {
    const id = value.id ?? value._id;
    return id != null ? String(id) : '';
  }
  return String(value);
};

const CustomerFormPage = () => {
  const { confirm } = useConfirmDialog();
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackRoute('/customers');
  const [formState, setFormState] = useState({
    adi: '',
    soyadi: '',
    telefon: '',
    mail: '',
    isTest: false,
  });
  
  // İl ve ilçe state'leri
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [illerLoading, setIllerLoading] = useState(false);
  const [ilcelerLoading, setIlcelerLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(CUSTOMER_TABS[0].key);
  
  // Adres listesi state'leri
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState('');
  
  // Adres ekleme/düzenleme modal state'leri
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    adres: '',
    cityId: '',
    districtId: '',
    varsayilan: false,
  });
  // Adres formu için il/ilçe state'leri (müşteri formundan ayrı)
  const [addressIller, setAddressIller] = useState([]);
  const [addressIlceler, setAddressIlceler] = useState([]);
  const [addressIllerLoading, setAddressIllerLoading] = useState(false);
  const [addressIlcelerLoading, setAddressIlcelerLoading] = useState(false);
  const [addressFormError, setAddressFormError] = useState('');
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  
  // Cihaz listesi state'leri
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState('');
  const [expandedDeviceIds, setExpandedDeviceIds] = useState(new Set());
  
  // Cihaz ekleme modal state'leri
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [deviceFormData, setDeviceFormData] = useState({
    urunId: '',
    seriNumarasi: '',
    kurulumTarihi: new Date(),
  });
  const [deviceFormError, setDeviceFormError] = useState('');
  const [deviceSubmitting, setDeviceSubmitting] = useState(false);
  
  // Ürün seçimi modal state'leri
  const [isProductSelectModalOpen, setIsProductSelectModalOpen] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState(null);
  const [selectedProductInfo, setSelectedProductInfo] = useState(null); // Seçili ürün bilgisini sakla (modal kapandıktan sonra da erişilebilir olsun)

  const [smsler, setSmsler] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsPage, setSmsPage] = useState(1);
  const [smsPagination, setSmsPagination] = useState({
    total: 0,
    totalPages: 1,
    mevcutSayfa: 1,
    limit: PAGE_DEFAULT_LIMIT,
  });
  const [smsDetayKayit, setSmsDetayKayit] = useState(null);
  const [cagriAltSekme, setCagriAltSekme] = useState('gelen');
  const [odemeEksikSerhi, setOdemeEksikSerhi] = useState(null);
  const [iletisimIzni, setIletisimIzni] = useState({ verildi: false });
  const [kvkkLoading, setKvkkLoading] = useState(false);
  const [kvkkError, setKvkkError] = useState('');
  const [kvkkKayitlar, setKvkkKayitlar] = useState([]);

  // Müşteri bilgilerini yükle
  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetchCustomerDetail(id);
        console.log('[CustomerFormPage] fetchCustomerDetail response:', response);
        
        // Farklı response formatlarını kontrol et
        const customer = response?.veri?.musteri || response?.veri || response?.musteri || null;
        
        if (customer) {
          console.log('[CustomerFormPage] Customer data:', customer);
          // Backend'den gelen alan isimlerini frontend alan isimlerine map et
          // Backend: name, surname, phone, address, mail
          // Frontend: adi, soyadi, telefon, adres, mail
          setFormState({
            adi: customer.name || customer.adi || '',
            soyadi: customer.surname || customer.soyadi || '',
            telefon: customer.phone || customer.telefon || '',
            mail: customer.mail || '',
            isTest: customer.isTest === true,
          });
          setOdemeEksikSerhi(customer.odemeEksikSerhi || null);
          setIletisimIzni(customer.iletisimIzni || { verildi: false });
          
          // Artık il/ilçe müşteri formunda değil, adreslerde
        } else {
          console.error('[CustomerFormPage] Customer not found in response:', response);
          throw new Error('Müşteri bulunamadı');
        }
      } catch (error) {
        console.error('Müşteri yüklenemedi:', error);
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(error, 'Müşteri yüklenemedi'));
        }
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    };
    
    loadCustomer();
  }, [id, navigate]);

  // İlleri yükle
  useEffect(() => {
    const loadCities = async () => {
      setIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setIller([]);
      } finally {
        setIllerLoading(false);
      }
    };
    
    loadCities();
  }, []);

  // İlçeleri yükle
  const loadDistricts = useCallback(async (cityId) => {
    if (!cityId || isNaN(cityId)) {
      setIlceler([]);
      return;
    }
    
    setIlcelerLoading(true);
    try {
      const response = await fetchDistrictsByCity(cityId);
      let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri || [];
      if (!Array.isArray(list)) {
        list = [];
      }
      
      // Mongoose document'lerini düz objeye çevir (_doc içindeki verileri kullan)
      const serializedList = list.map((ilce) => {
        // Eğer Mongoose document ise (_doc varsa), _doc içindeki veriyi kullan
        if (ilce._doc) {
          return {
            id: ilce._doc._id || ilce._doc.id,
            name: ilce._doc.name,
            ad: ilce._doc.ad,
            cityId: ilce._doc.cityId
          };
        }
        // Eğer zaten düz obje ise, olduğu gibi kullan
        return {
          id: ilce._id || ilce.id,
          name: ilce.name,
          ad: ilce.ad,
          cityId: ilce.cityId
        };
      });
      
      setIlceler(serializedList);
    } catch (err) {
      console.error('İlçeler yüklenemedi:', err);
      setIlceler([]);
    } finally {
      setIlcelerLoading(false);
    }
  }, []);

  // İl değiştiğinde ilçeleri yükle
  const prevCityIdRef = useRef(null);
  useEffect(() => {
    if (formState.cityId) {
      loadDistricts(formState.cityId);
      // İl değiştiğinde ilçe seçimini sıfırla (sadece gerçekten değiştiyse)
      // İlk yüklemede (prevCityIdRef.current === null) sıfırlama
      if (prevCityIdRef.current !== null && prevCityIdRef.current !== formState.cityId) {
        setFormState(prev => ({ ...prev, districtId: '' }));
      }
      prevCityIdRef.current = formState.cityId;
    } else {
      setIlceler([]);
      prevCityIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.cityId]);

  // Cihazları yükle
  const loadDevices = useCallback(async () => {
    console.log('[CustomerFormPage] loadDevices called with id:', id, 'activeTab:', activeTab);
    
    // Eğer devices sekmesi aktif değilse, çık
    if (activeTab !== 'devices') {
      console.log('[CustomerFormPage] loadDevices: Not devices tab, skipping');
      return;
    }
    
    // id kontrolü - eğer id yoksa veya boşsa, hata fırlatma, sadece çık
    const customerId = id ? String(id).trim() : '';
    if (!customerId || customerId === '') {
      console.warn('[CustomerFormPage] loadDevices: Customer ID is missing or empty, id:', id);
      setDevicesError('');
      setDevices([]);
      setDevicesLoading(false);
      return;
    }
    
    setDevicesLoading(true);
    setDevicesError('');
    try {
      console.log('[CustomerFormPage] loadDevices: Fetching devices for customer ID:', customerId);
      const response = await fetchDevicesByCustomer(customerId, { pasifDahil: true });
      console.log('[CustomerFormPage] fetchDevicesByCustomer response:', response);
      
      // Response formatını kontrol et
      const list = response?.veri?.cihazlar || response?.veri?.devices || response?.veri || [];
      const devicesList = Array.isArray(list) ? list : [];
      
      console.log('[CustomerFormPage] loadDevices: Devices list:', devicesList);
      setDevices(devicesList);
      
      // Eğer liste boşsa, hata değil normal durum
      if (devicesList.length === 0) {
        setDevicesError(''); // Hata mesajını temizle
      }
    } catch (err) {
      console.error('[CustomerFormPage] Cihazlar yüklenemedi:', err);
      
      // Hata mesajını kullanıcı dostu hale getir
      let errorMessage = getErrorMessage(err, 'Cihazlar yüklenemedi');
      
      // "ID gereklidir" veya "Müşteri bilgisi bulunamadı" gibi hataları kontrol et
      if (errorMessage.includes('ID gereklidir') || 
          errorMessage.includes('id gereklidir') || 
          errorMessage.includes('Müşteri bilgisi bulunamadı')) {
        // Eğer customerId varsa ama API hatası varsa, farklı mesaj göster
        if (customerId && customerId !== '') {
          errorMessage = 'Cihazlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.';
        } else {
          errorMessage = 'Müşteri bilgisi bulunamadı. Lütfen sayfayı yenileyin.';
        }
      }
      
      setDevicesError(errorMessage);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, [id, activeTab]); // id değiştiğinde yeniden yükle

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Adresleri yükle
  const loadAddresses = useCallback(async () => {
    if (activeTab !== 'addresses' || !id) {
      return;
    }
    
    setAddressesLoading(true);
    setAddressesError('');
    try {
      const response = await fetchAddressesByCustomer(id);
      const addressesList = response?.veri?.adresler || [];
      setAddresses(Array.isArray(addressesList) ? addressesList : []);
    } catch (err) {
      console.error('[CustomerFormPage] Adresler yüklenemedi:', err);
      setAddressesError(getErrorMessage(err, 'Adresler yüklenemedi'));
      setAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  }, [id, activeTab]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const loadCustomerSms = useCallback(async () => {
    if (!id || activeTab !== 'sms') {
      return;
    }

    setSmsLoading(true);
    setSmsError('');
    try {
      const response = await getCustomerSms(id, {
        sayfa: smsPage,
        limit: PAGE_DEFAULT_LIMIT,
      });

      if (response.basarili) {
        setSmsler(response.veri?.smsler || []);
        setSmsPagination(normalizeSayfalama(response.veri?.sayfalama, PAGE_DEFAULT_LIMIT));
      } else {
        setSmsError(response.mesaj || 'SMS kayıtları yüklenemedi');
        setSmsler([]);
      }
    } catch (error) {
      setSmsError(getErrorMessage(error, 'SMS kayıtları yüklenemedi'));
      setSmsler([]);
    } finally {
      setSmsLoading(false);
    }
  }, [id, activeTab, smsPage]);

  useEffect(() => {
    loadCustomerSms();
  }, [loadCustomerSms]);

  const loadKvkkGecmisi = useCallback(async () => {
    if (!id || activeTab !== 'kvkk') {
      return;
    }

    setKvkkLoading(true);
    setKvkkError('');
    try {
      const response = await fetchMusteriIletisimIzniGecmisi(id);
      if (response.basarili) {
        setKvkkKayitlar(response.veri?.kayitlar || []);
        if (response.veri?.guncelDurum) {
          setIletisimIzni(response.veri.guncelDurum);
        }
      } else {
        setKvkkError(response.mesaj || 'KVKK geçmişi yüklenemedi');
        setKvkkKayitlar([]);
      }
    } catch (error) {
      setKvkkError(getErrorMessage(error, 'KVKK geçmişi yüklenemedi'));
      setKvkkKayitlar([]);
    } finally {
      setKvkkLoading(false);
    }
  }, [id, activeTab]);

  useEffect(() => {
    loadKvkkGecmisi();
  }, [loadKvkkGecmisi]);

  const handleSmsPageChange = (newPage) => {
    setSmsPage(clampPage(newPage, smsPagination.totalPages));
  };

  // Adres formu için illeri yükle
  useEffect(() => {
    const loadAddressCities = async () => {
      setAddressIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setAddressIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Adres formu için iller yüklenemedi:', err);
        setAddressIller([]);
      } finally {
        setAddressIllerLoading(false);
      }
    };
    
    loadAddressCities();
  }, []);

  // Adres formu için ilçeleri yükle
  const loadAddressDistricts = useCallback(async (cityId) => {
    if (!cityId || isNaN(cityId)) {
      setAddressIlceler([]);
      return;
    }
    
    setAddressIlcelerLoading(true);
    try {
      const response = await fetchDistrictsByCity(cityId);
      let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri || [];
      
      if (!Array.isArray(list)) {
        list = [];
      }
      
      // API formatı: { id: number, adi: string, cityId: number }
      const serializedList = list.map((ilce) => {
        return {
          id: ilce.id || ilce._id,
          adi: ilce.adi || ilce.ad || ilce.name,
          ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
          name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
          cityId: ilce.cityId
        };
      });
      
      setAddressIlceler(serializedList);
    } catch (err) {
      console.error('Adres formu için ilçeler yüklenemedi:', err);
      setAddressIlceler([]);
    } finally {
      setAddressIlcelerLoading(false);
    }
  }, []);

  // Adres formu için il değiştiğinde ilçeleri yükle
  useEffect(() => {
    if (addressFormData.cityId && addressFormData.cityId.trim() !== '') {
      const cityIdNum = parseInt(addressFormData.cityId, 10);
      if (!isNaN(cityIdNum)) {
        loadAddressDistricts(cityIdNum);
      } else {
        setAddressIlceler([]);
      }
    } else {
      setAddressIlceler([]);
    }
  }, [addressFormData.cityId, loadAddressDistricts]);

  // Yeni adres ekle modal'ını aç
  const handleOpenAddressModal = () => {
    setEditingAddressId(null);
    setAddressFormData({
      adres: '',
      cityId: '',
      districtId: '',
      varsayilan: addresses.length === 0, // İlk adres ise otomatik varsayılan
    });
    setAddressFormError('');
    setIsAddressModalOpen(true);
  };

  // Adres düzenle modal'ını aç
  const handleEditAddress = (address) => {
    setEditingAddressId(address._id || address.id);

    const addressCityId = resolveLocationId(address.cityId ?? address.il?.id ?? address.city?.id);
    const addressDistrictId = resolveLocationId(address.districtId ?? address.ilce?.id ?? address.district?.id);

    if (addressCityId) {
      loadAddressDistricts(parseInt(addressCityId, 10));
    }

    setAddressFormData({
      adres: address.adres || '',
      cityId: addressCityId,
      districtId: addressDistrictId,
      varsayilan: address.varsayilan === true,
    });
    setAddressFormError('');
    setIsAddressModalOpen(true);
  };

  // Adres modal'ını kapat
  const handleCloseAddressModal = () => {
    setIsAddressModalOpen(false);
    setEditingAddressId(null);
    setAddressFormData({
      adres: '',
      cityId: '',
      districtId: '',
      varsayilan: false,
    });
    setAddressFormError('');
    setAddressIlceler([]);
  };

  // Adres kaydet (ekle veya güncelle)
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setAddressFormError('');
    
    if (!addressFormData.adres || addressFormData.adres.trim() === '') {
      setAddressFormError('Adres gereklidir');
      return;
    }
    
    if (addressFormData.adres.trim().length < 5) {
      setAddressFormError('Adres en az 5 karakter olmalıdır');
      return;
    }
    
    if (addressFormData.adres.trim().length > 500) {
      setAddressFormError('Adres en fazla 500 karakter olabilir');
      return;
    }
    
    setAddressSubmitting(true);
    try {
      if (editingAddressId) {
        // Güncelle
        await updateAddress(id, editingAddressId, addressFormData);
        if (window.showNotification) {
          window.showNotification('success', 'Adres güncellendi');
        }
      } else {
        // Ekle
        await createAddress(id, addressFormData);
        if (window.showNotification) {
          window.showNotification('success', 'Adres eklendi');
        }
      }
      
      handleCloseAddressModal();
      loadAddresses(); // Adresleri yeniden yükle
    } catch (error) {
      console.error('Adres kaydetme hatası:', error);
      setAddressFormError(getErrorMessage(error, 'Adres kaydedilemedi'));
    } finally {
      setAddressSubmitting(false);
    }
  };

  // Adres sil
  const handleDeleteAddress = async (addressId) => {
    const ok = await confirm({
      title: 'Adresi sil',
      message: 'Bu adresi silmek istediğinize emin misiniz?',
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) {
      return;
    }
    
    try {
      await deleteAddress(id, addressId);
      if (window.showNotification) {
        window.showNotification('success', 'Adres silindi');
      }
      loadAddresses(); // Adresleri yeniden yükle
    } catch (error) {
      console.error('Adres silme hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Adres silinemedi'));
      }
    }
  };

  // Adresi varsayılan yap
  const handleSetDefaultAddress = async (addressId) => {
    try {
      await setDefaultAddress(id, addressId);
      if (window.showNotification) {
        window.showNotification('success', 'Varsayılan adres güncellendi');
      }
      loadAddresses(); // Adresleri yeniden yükle
    } catch (error) {
      console.error('Varsayılan adres ayarlama hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Varsayılan adres ayarlanamadı'));
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    
    // İl değiştiğinde ilçeyi temizle
    if (name === 'cityId') {
      setFormState((prev) => ({
        ...prev,
        cityId: value,
        districtId: '', // İl değiştiğinde ilçeyi sıfırla
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
    }
    
    if (formError) setFormError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validasyon
    if (!formState.adi.trim()) {
      setFormError('Ad zorunludur.');
      return;
    }
    if (!formState.soyadi.trim()) {
      setFormError('Soyad zorunludur.');
      return;
    }
    if (!formState.telefon.trim()) {
      setFormError('Telefon zorunludur.');
      return;
    }
    
    // İl ve ilçe artık müşteri formunda yok, adreslerde olacak

    setFormSubmitting(true);
    try {
      const response = await updateCustomer(id, formState);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Müşteri güncellenemedi');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Müşteri bilgileri güncellendi');
      }
      
      navigate('/customers');
    } catch (error) {
      console.error('Müşteri güncelleme hatası:', error);
      setFormError(getErrorMessage(error, 'Müşteri güncellenemedi'));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleOpenDeviceModal = () => {
    setIsDeviceModalOpen(true);
    setDeviceFormData({
      urunId: '',
      seriNumarasi: '',
      kurulumTarihi: new Date(),
    });
    setDeviceFormError('');
    setSelectedProductInfo(null); // Yeni cihaz ekleme için seçili ürün bilgisini temizle
  };

  const handleCloseDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setDeviceFormData({
      urunId: '',
      seriNumarasi: '',
      kurulumTarihi: new Date(),
    });
    setDeviceFormError('');
    setSelectedProductForModal(null);
    setSelectedProductInfo(null); // Modal kapandığında seçili ürün bilgisini temizle
  };

  const handleOpenProductSelectModal = () => {
    setIsProductSelectModalOpen(true);
    setSelectedProductForModal(null);
  };

  const handleCloseProductSelectModal = () => {
    setIsProductSelectModalOpen(false);
    setSelectedProductForModal(null);
  };

  const handleProductSelect = (product) => {
    setSelectedProductForModal(product);
  };

  const handleProductConfirm = () => {
    if (selectedProductForModal) {
      const productId = selectedProductForModal.id || selectedProductForModal._id;
      setDeviceFormData((prev) => ({
        ...prev,
        urunId: productId,
      }));
      // Seçili ürün bilgisini sakla (modal kapandıktan sonra da görüntülenebilsin)
      setSelectedProductInfo(selectedProductForModal);
      handleCloseProductSelectModal();
    }
  };

  const handleDeviceSubmit = async (e) => {
    e.preventDefault();
    setDeviceFormError('');
    
    // Validasyon
    if (!deviceFormData.urunId) {
      setDeviceFormError('Ürün seçimi zorunludur.');
      return;
    }
    if (!deviceFormData.seriNumarasi.trim()) {
      setDeviceFormError('Seri numarası zorunludur.');
      return;
    }

    setDeviceSubmitting(true);
    try {
      const payload = {
        musteriId: id,
        urunId: deviceFormData.urunId,
        seriNumarasi: deviceFormData.seriNumarasi.trim(),
        garantiBaslangicTarihi: deviceFormData.kurulumTarihi,
      };

      const response = await createDevice(payload);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Cihaz oluşturulamadı');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Cihaz başarıyla eklendi');
      }
      
      handleCloseDeviceModal();
      loadDevices();
    } catch (error) {
      console.error('Cihaz oluşturma hatası:', error);
      setDeviceFormError(getErrorMessage(error, 'Cihaz oluşturulamadı'));
    } finally {
      setDeviceSubmitting(false);
    }
  };

  const toggleDeviceExpand = (deviceId) => {
    setExpandedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const handleDeleteDevice = async (device) => {
    const deviceId = device.id || device._id;
    const confirmed = await confirm({
      title: 'Cihazı sil',
      message: 'Bu cihazı silmek istediğinize emin misiniz?',
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!confirmed) return;

    try {
      const response = await deleteDevice(deviceId);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Cihaz silinemedi');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Cihaz başarıyla silindi');
      }
      
      loadDevices();
    } catch (error) {
      console.error('Cihaz silme hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Cihaz silinemedi'));
      }
    }
  };

  // Response'tan ürün adını al (backward compatibility için hem yeni hem eski field adlarını kontrol eder)
  const getProductName = (product) => {
    if (!product) return '';
    return product.icerikAdi || product.urunAdi || product.ad || '';
  };

  // Response'tan ürün kodunu al (backward compatibility için hem yeni hem eski field adlarını kontrol eder)
  const getProductCode = (product) => {
    if (!product) return '';
    return product.icerikKodu || product.urunKodu || product.kod || '';
  };

  const getSelectedProductName = () => {
    if (!deviceFormData.urunId) return '';
    
    // Önce saklanan seçili ürün bilgisini kontrol et
    if (selectedProductInfo && (selectedProductInfo.id || selectedProductInfo._id) === deviceFormData.urunId) {
      return getProductName(selectedProductInfo);
    }

    return '';
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Müşteri bilgileri yükleniyor</div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="content-header">
        <div>
          <h1>Müşteri Düzenle</h1>
        </div>
      </div>

      {odemeEksikSerhi?.aktif === true && (
        <div
          style={{
            marginBottom: '20px',
            padding: '14px 16px',
            borderRadius: '10px',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: '14px',
          }}
        >
          <strong><i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>Ödeme eksikliği şerhi aktif</strong>
          <div style={{ marginTop: '6px' }}>
            Bu müşteride önceki randevuda alınamayan tahsilat kaydı bulunmaktadır. Yeni servis kaydı açılırken ödeme beyanı zorunludur.
          </div>
        </div>
      )}

      <div className="tabs">
        {CUSTOMER_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === 'sms') {
                setSmsPage(1);
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {formError && <div className="error-state small">{safeText(formError)}</div>}

      {activeTab === 'info' && (
        <div className="tab-panel">
          <div className="tab-panel-card">
            <div className="section-block">
              <div className="section-title">Müşteri Bilgileri</div>
              <form onSubmit={handleFormSubmit}>
                <div className="form-grid">
                  <label className="form-field">
                    <span>Ad *</span>
                    <input
                      type="text"
                      name="adi"
                      value={formState.adi}
                      onChange={handleFormChange}
                      maxLength={100}
                      placeholder="Müşteri adı"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Soyad *</span>
                    <input
                      type="text"
                      name="soyadi"
                      value={formState.soyadi}
                      onChange={handleFormChange}
                      maxLength={100}
                      placeholder="Müşteri soyadı"
                      required
                    />
                  </label>
                </div>
                <div className="form-grid">
                  <label className="form-field">
                    <span>Telefon *</span>
                    <input
                      type="text"
                      name="telefon"
                      value={formState.telefon}
                      onChange={handleFormChange}
                      maxLength={50}
                      placeholder="Telefon numarası"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>E-posta</span>
                    <input
                      type="email"
                      name="mail"
                      value={formState.mail}
                      onChange={handleFormChange}
                      maxLength={100}
                      placeholder="E-posta adresi"
                    />
                  </label>
                </div>
                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="isTest"
                    checked={formState.isTest === true}
                    onChange={handleFormChange}
                  />
                  <span>Test müşterisi</span>
                </label>
                <div className="section-actions">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={goBack}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={formSubmitting}
                  >
                    {formSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Kaydediliyor...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        <span>Kaydet</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'addresses' && (
        <div className="tab-panel">
          <div className="categories-section">
            <div className="categories-header">
              <h2 className="categories-title">Adres Listesi</h2>
              <button
                type="button"
                className="primary-btn add-category-btn"
                onClick={handleOpenAddressModal}
              >
                <i className="fas fa-plus"></i>
                <span>Yeni Adres Ekle</span>
              </button>
            </div>
            
            <div className="categories-content">
              {addressesLoading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Adresler yükleniyor...</span>
                </div>
              ) : addressesError ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{addressesError}</span>
                </div>
              ) : addresses.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox"></i>
                  <span>Henüz adres bulunmamaktadır.</span>
                </div>
              ) : (
                <div className="table-container">
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>Adres</th>
                        <th>Durum</th>
                        <th>Varsayılan</th>
                        <th>Eklenme Tarihi</th>
                        <th className="islemler-sutunu">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addresses.map((address) => {
                        const addressId = address._id || address.id;
                        const isDefault = address.varsayilan === true;
                        const isActive = address.aktif !== false;
                        const canSetDefault = addresses.length > 1;
                        
                        return (
                          <tr key={addressId}>
                            <td>
                              <div style={{ marginBottom: '4px' }}>{address.adres || '—'}</div>
                              {address.city || address.il || address.district || address.ilce || address.cityId || address.districtId ? (
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                  {(() => {
                                    // İl ve ilçe bilgilerini göster
                                    const ilAd = address.city?.name || address.city?.ad || address.il?.name || address.il?.ad || 
                                                 (address.cityId && addressIller.find(i => String(i.id) === String(address.cityId))?.name) ||
                                                 (address.cityId && addressIller.find(i => String(i.id) === String(address.cityId))?.ad) || '';
                                    const ilceAd = address.district?.name || address.district?.ad || address.ilce?.name || address.ilce?.ad || 
                                                   (address.districtId && addressIlceler.find(i => String(i.id) === String(address.districtId))?.name) ||
                                                   (address.districtId && addressIlceler.find(i => String(i.id) === String(address.districtId))?.ad) || '';
                                    
                                    if (ilAd && ilceAd) {
                                      return `${ilceAd} / ${ilAd}`;
                                    } else if (ilAd) {
                                      return ilAd;
                                    } else if (ilceAd) {
                                      return ilceAd;
                                    }
                                    return '';
                                  })()}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {isActive ? (
                                <span className="status-badge active">Aktif</span>
                              ) : (
                                <span className="status-badge inactive">Pasif</span>
                              )}
                            </td>
                            <td>
                              {isDefault ? (
                                <span className="status-badge default">Varsayılan</span>
                              ) : (
                                <span>—</span>
                              )}
                            </td>
                            <td>
                              {address.createdAt
                                ? new Date(address.createdAt).toLocaleDateString('tr-TR')
                                : '—'}
                            </td>
                            <td className="table-actions islemler-sutunu">
                              <button
                                type="button"
                                className="category-action-btn edit-btn"
                                onClick={() => handleEditAddress(address)}
                                title="Düzenle"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              {canSetDefault && !isDefault && (
                                <button
                                  type="button"
                                  className="category-action-btn move-btn"
                                  onClick={() => handleSetDefaultAddress(addressId)}
                                  title="Varsayılan Yap"
                                >
                                  <i className="fas fa-star"></i>
                                </button>
                              )}
                              {addresses.length > 1 && (
                                <button
                                  type="button"
                                  className="category-action-btn delete-btn"
                                  onClick={() => handleDeleteAddress(addressId)}
                                  title="Sil"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'devices' && (
        <div className="tab-panel">
          <div className="categories-section">
            <div className="categories-header">
              <h2 className="categories-title">Cihaz Listesi</h2>
              <button
                type="button"
                className="primary-btn add-category-btn"
                onClick={handleOpenDeviceModal}
              >
                <i className="fas fa-plus"></i>
                <span>Yeni Cihaz Ekle</span>
              </button>
            </div>

            <div className="categories-content">
              {devicesLoading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Cihazlar yükleniyor...</span>
                </div>
              ) : devicesError ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{devicesError}</span>
                </div>
              ) : (
                <>
                  {/* Aktif Cihazlar */}
                  <div style={{ marginBottom: 32 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                      Aktif Cihazlar ({devices.filter((d) => d.aktif !== false).length})
                    </h3>
                    {devices.filter((d) => d.aktif !== false).length === 0 ? (
                      <div className="empty-state" style={{ padding: 24 }}>
                        <span>Aktif cihaz bulunmamaktadır.</span>
                      </div>
                    ) : (
                      <div className="table-container">
                        <table className="admins-table">
                          <thead>
                            <tr>
                              <th>Ürün Adı</th>
                              <th>Seri Numarası</th>
                              <th>Kurulum Tarihi</th>
                              <th>Garanti Bitiş Tarihi</th>
                              <th className="islemler-sutunu">İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {devices.filter((d) => d.aktif !== false).map((device) => {
                        const deviceId = device.id || device._id;
                        const parcaGarantiler = Array.isArray(device.parcaGarantiler) ? device.parcaGarantiler : [];
                        const isExpanded = expandedDeviceIds.has(deviceId);
                        // Garanti durumunu kontrol et
                        const garantiBitisTarihi = device.garantiBitisTarihi 
                          ? new Date(device.garantiBitisTarihi) 
                          : null;
                        const bugun = new Date();
                        bugun.setHours(0, 0, 0, 0); // Saat bilgisini sıfırla
                        
                        let garantiDurumu = 'unknown'; // 'active', 'expired', 'unknown'
                        if (garantiBitisTarihi) {
                          garantiBitisTarihi.setHours(0, 0, 0, 0);
                          garantiDurumu = garantiBitisTarihi >= bugun ? 'active' : 'expired';
                        }
                        
                        const garantiRengi = garantiDurumu === 'active' ? '#10b981' : garantiDurumu === 'expired' ? '#ef4444' : '#6b7280';
                        
                        return (
                          <React.Fragment key={deviceId}>
                            <tr>
                              <td>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  flexWrap: 'nowrap'
                                }}>
                                  <button
                                    type="button"
                                    onClick={() => toggleDeviceExpand(deviceId)}
                                    className="category-action-btn"
                                    style={{
                                      flexShrink: 0,
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '2px 4px',
                                      color: '#64748b'
                                    }}
                                    title={isExpanded ? 'Parça garantilerini kapat' : 'Parça garantilerini göster'}
                                  >
                                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '10px' }}></i>
                                  </button>
                                  <div
                                    style={{
                                      flexShrink: 0,
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: garantiRengi
                                    }}
                                    title={garantiDurumu === 'active' ? 'Garanti devam ediyor' : garantiDurumu === 'expired' ? 'Garanti süresi dolmuş' : 'Garanti bilgisi bulunamadı'}
                                  />
                                  <span style={{ flex: '1', minWidth: 0 }}>{device.urun ? getProductName(device.urun) : '—'}</span>
                                  {device.eskiKayit ? <DeviceEskiKayitBadge /> : null}
                                </div>
                              </td>
                              <td>{device.seriNumarasi || '—'}</td>
                              <td>
                                {(device.kurulumTarihi || device.garantiBaslangicTarihi)
                                  ? new Date(device.kurulumTarihi || device.garantiBaslangicTarihi).toLocaleDateString('tr-TR')
                                  : '—'}
                              </td>
                              <td>
                                {device.garantiBitisTarihi
                                  ? new Date(device.garantiBitisTarihi).toLocaleDateString('tr-TR')
                                  : '—'}
                              </td>
                              <td className="table-actions islemler-sutunu">
                                <button
                                  type="button"
                                  className="category-action-btn delete-btn"
                                  onClick={() => handleDeleteDevice(device)}
                                  title="Sil"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} style={{ padding: 0, verticalAlign: 'top', borderTop: 'none' }}>
                                  <div style={{
                                    padding: '12px 16px 12px 48px',
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '1px solid #e2e8f0'
                                  }}>
                                    <DeviceKurulumRandevuLink device={device} />
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>
                                      Parça Garantileri (6 Ay)
                                    </div>
                                    {parcaGarantiler.length === 0 ? (
                                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>Parça garantisi bulunmuyor</div>
                                    ) : (
                                      <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                          <thead>
                                            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Parça Adı</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Kod</th>
                                              <th style={{ textAlign: 'center', padding: '8px', color: '#64748b' }}>Adet</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Garanti Başlangıç</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Garanti Bitiş</th>
                                              <th style={{ textAlign: 'center', padding: '8px', color: '#64748b' }}>Durum</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {parcaGarantiler.map((pg) => {
                                              const pgBitis = pg.garantiBitisTarihi ? new Date(pg.garantiBitisTarihi) : null;
                                              const pgBugun = new Date();
                                              pgBugun.setHours(0, 0, 0, 0);
                                              if (pgBitis) pgBitis.setHours(0, 0, 0, 0);
                                              const pgDurum = pgBitis ? (pgBitis >= pgBugun ? 'active' : 'expired') : 'unknown';
                                              const pgRengi = pgDurum === 'active' ? '#10b981' : pgDurum === 'expired' ? '#ef4444' : '#6b7280';
                                              return (
                                                <tr key={pg.id || pg._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                  <td style={{ padding: '8px' }}>{pg.parcaAdi || '—'}</td>
                                                  <td style={{ padding: '8px' }}>{pg.parcaKodu || '—'}</td>
                                                  <td style={{ padding: '8px', textAlign: 'center' }}>{pg.adet ?? '—'}</td>
                                                  <td style={{ padding: '8px' }}>
                                                    {pg.garantiBaslangicTarihi ? new Date(pg.garantiBaslangicTarihi).toLocaleDateString('tr-TR') : '—'}
                                                  </td>
                                                  <td style={{ padding: '8px' }}>
                                                    {pg.garantiBitisTarihi ? new Date(pg.garantiBitisTarihi).toLocaleDateString('tr-TR') : '—'}
                                                  </td>
                                                  <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <span
                                                      style={{
                                                        display: 'inline-block',
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: pgRengi,
                                                        title: pgDurum === 'active' ? 'Garanti devam ediyor' : pgDurum === 'expired' ? 'Garanti süresi dolmuş' : 'Bilinmiyor'
                                                      }}
                                                    ></span>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                    )}
                  </div>

                  {/* Pasif Cihazlar */}
                  <div style={{ marginBottom: 32 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i className="fas fa-ban" style={{ color: '#94a3b8' }}></i>
                      Pasif Cihazlar ({devices.filter((d) => d.aktif === false).length})
                    </h3>
                    {devices.filter((d) => d.aktif === false).length === 0 ? (
                      <div className="empty-state" style={{ padding: 24 }}>
                        <span>Pasif cihaz bulunmamaktadır.</span>
                      </div>
                    ) : (
                      <div className="table-container">
                        <table className="admins-table">
                          <thead>
                            <tr>
                              <th>Ürün Adı</th>
                              <th>Seri Numarası</th>
                              <th>Kurulum Tarihi</th>
                              <th>Garanti Bitiş Tarihi</th>
                              <th className="islemler-sutunu">İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {devices.filter((d) => d.aktif === false).map((device) => {
                        const deviceId = device.id || device._id;
                        const parcaGarantiler = Array.isArray(device.parcaGarantiler) ? device.parcaGarantiler : [];
                        const isExpanded = expandedDeviceIds.has(deviceId);
                        return (
                          <React.Fragment key={deviceId}>
                            <tr>
                              <td>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  flexWrap: 'nowrap'
                                }}>
                                  <button
                                    type="button"
                                    onClick={() => toggleDeviceExpand(deviceId)}
                                    className="category-action-btn"
                                    style={{
                                      flexShrink: 0,
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '2px 4px',
                                      color: '#64748b'
                                    }}
                                    title={isExpanded ? 'Parça garantilerini kapat' : 'Parça garantilerini göster'}
                                  >
                                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '10px' }}></i>
                                  </button>
                                  <span style={{ flex: '1', minWidth: 0 }}>{device.urun ? getProductName(device.urun) : '—'}</span>
                                  {device.eskiKayit ? <DeviceEskiKayitBadge /> : null}
                                </div>
                              </td>
                              <td>{device.seriNumarasi || '—'}</td>
                              <td>
                                {(device.kurulumTarihi || device.garantiBaslangicTarihi)
                                  ? new Date(device.kurulumTarihi || device.garantiBaslangicTarihi).toLocaleDateString('tr-TR')
                                  : '—'}
                              </td>
                              <td>
                                {device.garantiBitisTarihi
                                  ? new Date(device.garantiBitisTarihi).toLocaleDateString('tr-TR')
                                  : '—'}
                              </td>
                              <td className="table-actions islemler-sutunu">
                                <button
                                  type="button"
                                  className="category-action-btn delete-btn"
                                  onClick={() => handleDeleteDevice(device)}
                                  title="Sil"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} style={{ padding: 0, verticalAlign: 'top', borderTop: 'none' }}>
                                  <div style={{
                                    padding: '12px 16px 12px 48px',
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '1px solid #e2e8f0'
                                  }}>
                                    <DeviceKurulumRandevuLink device={device} />
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>
                                      Parça Garantileri (6 Ay)
                                    </div>
                                    {parcaGarantiler.length === 0 ? (
                                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>Parça garantisi bulunmuyor</div>
                                    ) : (
                                      <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                          <thead>
                                            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Parça Adı</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Kod</th>
                                              <th style={{ textAlign: 'center', padding: '8px', color: '#64748b' }}>Adet</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Garanti Başlangıç</th>
                                              <th style={{ textAlign: 'left', padding: '8px', color: '#64748b' }}>Garanti Bitiş</th>
                                              <th style={{ textAlign: 'center', padding: '8px', color: '#64748b' }}>Durum</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {parcaGarantiler.map((pg) => {
                                              const pgBitis = pg.garantiBitisTarihi ? new Date(pg.garantiBitisTarihi) : null;
                                              const pgBugun = new Date();
                                              pgBugun.setHours(0, 0, 0, 0);
                                              if (pgBitis) pgBitis.setHours(0, 0, 0, 0);
                                              const pgDurum = pgBitis ? (pgBitis >= pgBugun ? 'active' : 'expired') : 'unknown';
                                              const pgRengi = pgDurum === 'active' ? '#10b981' : pgDurum === 'expired' ? '#ef4444' : '#6b7280';
                                              return (
                                                <tr key={pg.id || pg._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                  <td style={{ padding: '8px' }}>{pg.parcaAdi || '—'}</td>
                                                  <td style={{ padding: '8px' }}>{pg.parcaKodu || '—'}</td>
                                                  <td style={{ padding: '8px', textAlign: 'center' }}>{pg.adet ?? '—'}</td>
                                                  <td style={{ padding: '8px' }}>
                                                    {pg.garantiBaslangicTarihi ? new Date(pg.garantiBaslangicTarihi).toLocaleDateString('tr-TR') : '—'}
                                                  </td>
                                                  <td style={{ padding: '8px' }}>
                                                    {pg.garantiBitisTarihi ? new Date(pg.garantiBitisTarihi).toLocaleDateString('tr-TR') : '—'}
                                                  </td>
                                                  <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <span
                                                      style={{
                                                        display: 'inline-block',
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: pgRengi,
                                                        title: pgDurum === 'active' ? 'Garanti devam ediyor' : pgDurum === 'expired' ? 'Garanti süresi dolmuş' : 'Bilinmiyor'
                                                      }}
                                                    ></span>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
              
                  {/* Garanti Durumu Bilgilendirme */}
              <div style={{ 
                marginTop: '20px', 
                padding: '12px 16px', 
                backgroundColor: '#f8fafc', 
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: 500, 
                  color: '#475569', 
                  marginBottom: '10px' 
                }}>
                  Garanti Durumu:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#10b981',
                        flexShrink: 0
                      }}
                    ></div>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Garanti devam ediyor</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#ef4444',
                        flexShrink: 0
                      }}
                    ></div>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Garanti süresi dolmuş</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#6b7280',
                        flexShrink: 0
                      }}
                    ></div>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Garanti bilgisi bulunamadı</span>
                  </div>
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sms' && (
        <div className="tab-panel">
          <div className="categories-section">
            <div className="categories-header">
              <h2 className="categories-title">SMS Geçmişi</h2>
              {formState.telefon ? (
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  Telefon: <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{safeText(formState.telefon)}</span>
                </span>
              ) : null}
            </div>

            <div className="categories-content">
              {smsLoading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>SMS kayıtları yükleniyor...</span>
                </div>
              ) : smsError ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{smsError}</span>
                </div>
              ) : !formState.telefon ? (
                <div className="empty-state">
                  <i className="fas fa-phone-slash"></i>
                  <span>Müşterinin telefon numarası tanımlı değil; SMS kaydı listelenemez.</span>
                </div>
              ) : smsler.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-sms"></i>
                  <span>Bu telefon numarasına gönderilmiş SMS kaydı bulunmuyor.</span>
                </div>
              ) : (
                <>
                  <div className="table-container">
                    <table className="admins-table">
                      <thead>
                        <tr>
                          <th>Gönderilme Zamanı</th>
                          <th>Tip</th>
                          <th>Mesaj</th>
                          <th>Durum</th>
                          <th>İlişkili Kayıt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {smsler.map((sms) => (
                          <tr key={sms.id}>
                            <td>{formatSmsDate(sms.olusturmaTarihi)}</td>
                            <td>{getSmsTipBadge(sms.tip)}</td>
                            <td style={{ maxWidth: '420px' }}>
                              <div
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  lineHeight: 1.5,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {safeText(sms.mesaj) || '—'}
                              </div>
                            </td>
                            <td>
                              <SmsDurumIkonlari
                                sms={sms}
                                onInfoClick={setSmsDetayKayit}
                              />
                            </td>
                            <td>
                              {sms.servisKaydiId && sms.servisKaydiNumara ? (
                                <button
                                  type="button"
                                  className="link-btn"
                                  onClick={() => navigate(`/service-records/${sms.servisKaydiId}/update`)}
                                >
                                  {safeText(sms.servisKaydiNumara)}
                                </button>
                              ) : sms.randevuNumarasi ? (
                                <span>{safeText(sms.randevuNumarasi)}</span>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <TableListFooter
                    totalRecords={smsPagination.total}
                    currentPage={smsPagination.mevcutSayfa || smsPage}
                    totalPages={smsPagination.totalPages}
                    onPageChange={handleSmsPageChange}
                    loading={smsLoading}
                    embedded
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'calls' && (
        <div className="tab-panel">
          <div className="categories-section">
            <div className="categories-header">
              <h2 className="categories-title">Çağrı Geçmişi</h2>
              {formState.telefon ? (
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  Telefon: <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{safeText(formState.telefon)}</span>
                </span>
              ) : null}
            </div>

            <div className="categories-content">
              {!formState.telefon ? (
                <div className="empty-state">
                  <i className="fas fa-phone-slash"></i>
                  <span>Telefon kayıtlı değil; çağrı geçmişi listelenemez.</span>
                </div>
              ) : (
                <>
                  <div className="mh-tabs" style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      className={`mh-tab${cagriAltSekme === 'gelen' ? ' is-active' : ''}`}
                      onClick={() => setCagriAltSekme('gelen')}
                    >
                      Gelen
                    </button>
                    <button
                      type="button"
                      className={`mh-tab${cagriAltSekme === 'giden' ? ' is-active' : ''}`}
                      onClick={() => setCagriAltSekme('giden')}
                    >
                      Giden
                    </button>
                  </div>

                  {cagriAltSekme === 'gelen' ? (
                    <MusteriCagriListesi
                      tip="gelen"
                      telefon={formState.telefon}
                      hideMusteriKolonu
                      hideTelefonFiltresi
                      aktif={activeTab === 'calls' && cagriAltSekme === 'gelen'}
                    />
                  ) : (
                    <MusteriCagriListesi
                      tip="giden"
                      telefon={formState.telefon}
                      hideMusteriKolonu
                      hideTelefonFiltresi
                      aktif={activeTab === 'calls' && cagriAltSekme === 'giden'}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'kvkk' && (
        <div className="tab-panel">
          <div className="categories-section">
            <div className="categories-header">
              <h2 className="categories-title">KVKK / İletişim İzni</h2>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {iletisimIzni?.verildi
                  ? `Verildi${iletisimIzni.dogrulamaTarihi ? ` — ${formatSmsDate(iletisimIzni.dogrulamaTarihi)}` : ''}`
                  : 'Henüz doğrulanmadı'}
              </span>
            </div>

            <div className="categories-content">
              {kvkkLoading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>KVKK kayıtları yükleniyor...</span>
                </div>
              ) : kvkkError ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{kvkkError}</span>
                </div>
              ) : kvkkKayitlar.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-shield-alt"></i>
                  <span>Doğrulanmış iletişim izni kaydı bulunmuyor.</span>
                </div>
              ) : (
                <div className="table-container">
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Telefon</th>
                        <th>Doğrulayan</th>
                        <th>Bağlam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kvkkKayitlar.map((kayit) => (
                        <tr key={kayit.id}>
                          <td>{formatSmsDate(kayit.dogrulamaTarihi)}</td>
                          <td style={{ fontFamily: 'monospace' }}>{safeText(kayit.telefon) || '—'}</td>
                          <td>{safeText(kayit.dogrulayanAd) || '—'}</td>
                          <td>
                            {kayit.servisKaydi?.id && kayit.servisKaydi?.numara ? (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => navigate(`/service-records/${kayit.servisKaydi.id}/update`)}
                              >
                                {safeText(kayit.servisKaydi.numara)}
                              </button>
                            ) : kayit.gorusmeKaydi?.id ? (
                              `Görüşme — ${formatSmsDate(kayit.gorusmeKaydi.olusturmaTarihi)}`
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cihaz Ekleme Modal */}
      {isDeviceModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseDeviceModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div 
              className="modal-header"
              style={{
                padding: '24px 28px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
              }}
            >
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1e293b',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <i className="fas fa-mobile-alt" style={{ color: '#2563eb' }}></i>
                Yeni Cihaz Ekle
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseDeviceModal}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form 
              className="modal-body" 
              onSubmit={handleDeviceSubmit}
              style={{
                padding: '28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                overflowY: 'auto',
                flex: 1
              }}
            >
              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-box" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  Ürün *
                </label>
                {deviceFormData.urunId ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        Seçili Ürün:
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b' }}>
                        {getSelectedProductName()}
                      </div>
                      {selectedProductInfo &&
                        (selectedProductInfo.id || selectedProductInfo._id) === deviceFormData.urunId &&
                        getProductCode(selectedProductInfo) ? (
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#64748b',
                            marginTop: '6px',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          }}
                        >
                          Kod: {getProductCode(selectedProductInfo)}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenProductSelectModal}
                      title="Ürün Değiştir"
                      style={{
                        background: 'transparent',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        color: '#64748b',
                        fontSize: '13px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.color = '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.color = '#64748b';
                      }}
                    >
                      <i className="fas fa-edit"></i>
                      Değiştir
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleOpenProductSelectModal}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#64748b',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.color = '#64748b';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <span>Ürün Seç</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                )}
              </div>

              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label htmlFor="seri-numarasi" style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-barcode" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  Seri Numarası *
                </label>
                <input
                  id="seri-numarasi"
                  type="text"
                  className="form-control"
                  value={deviceFormData.seriNumarasi}
                  onChange={(e) =>
                    setDeviceFormData({ ...deviceFormData, seriNumarasi: e.target.value })
                  }
                  placeholder="Seri numarasını girin"
                  maxLength={100}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#ffffff',
                    color: '#1e293b',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-calendar" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  Kurulum Tarihi *
                </label>
                <div style={{ position: 'relative' }}>
                  <DatePicker
                    selected={deviceFormData.kurulumTarihi}
                    onChange={(date) => setDeviceFormData({ ...deviceFormData, kurulumTarihi: date })}
                    dateFormat="dd/MM/yyyy"
                    maxDate={new Date()}
                    placeholderText="Kurulum tarihini seçin"
                    required
                    className="form-control"
                    wrapperClassName="react-datepicker-wrapper"
                    withPortal
                    portalId="root-portal"
                    popperPlacement="bottom-start"
                    popperModifiers={[
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                        },
                      },
                    ]}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      background: '#ffffff',
                      color: '#1e293b',
                      transition: 'all 0.2s ease',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-info-circle" style={{ fontSize: '11px' }}></i>
                  Garanti süresi bu tarihten başlayacaktır
                </div>
              </div>

              {selectedProductInfo ? (
                <div className="form-group" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <i className="fas fa-shield-alt" style={{ fontSize: '12px', color: '#64748b' }}></i>
                    Ürün garantisi (kurulum sonrası)
                  </label>
                  <div
                    className="form-control"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      background: '#f8fafc',
                      color: '#1e293b',
                    }}
                  >
                    {selectedProductInfo.kurulumGarantiSuresiAy != null &&
                    Number.isFinite(Number(selectedProductInfo.kurulumGarantiSuresiAy))
                      ? `${Number(selectedProductInfo.kurulumGarantiSuresiAy)} ay (ürün içeriğinde tanımlı)`
                      : 'Ürün içeriğinde süre yok — kurulumda sunucu varsayılanı (24 ay) uygulanır'}
                  </div>
                </div>
              ) : null}

              {deviceFormError && (
                <div className="form-error" style={{
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  color: '#dc2626',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{deviceFormError}</span>
                </div>
              )}

              <div 
                className="modal-footer"
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e2e8f0',
                  marginTop: '8px',
                  paddingTop: '20px'
                }}
              >
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleCloseDeviceModal}
                  disabled={deviceSubmitting}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#64748b',
                    cursor: deviceSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: deviceSubmitting ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!deviceSubmitting) {
                      e.target.style.backgroundColor = '#f8fafc';
                      e.target.style.borderColor = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!deviceSubmitting) {
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  <i className="fas fa-times"></i>
                  İptal
                </button>
                <button 
                  type="submit" 
                  className="primary-btn" 
                  disabled={deviceSubmitting}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: 'none',
                    background: deviceSubmitting ? '#94a3b8' : '#2563eb',
                    color: '#ffffff',
                    cursor: deviceSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: deviceSubmitting ? 'none' : '0 2px 8px rgba(37, 99, 235, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!deviceSubmitting) {
                      e.target.style.backgroundColor = '#1d4ed8';
                      e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!deviceSubmitting) {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {deviceSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adres Ekleme/Düzenleme Modal */}
      {isAddressModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseAddressModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div 
              className="modal-header"
              style={{
                padding: '24px 28px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
              }}
            >
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1e293b',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <i className="fas fa-map-marker-alt" style={{ color: '#2563eb' }}></i>
                {editingAddressId ? 'Adres Düzenle' : 'Yeni Adres Ekle'}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseAddressModal}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form 
              className="modal-body" 
              onSubmit={handleSaveAddress}
              style={{
                padding: '28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                overflowY: 'auto',
                flex: 1
              }}
            >
              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label 
                  htmlFor="address-text"
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fas fa-map" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  Adres *
                </label>
                <textarea
                  id="address-text"
                  className="form-control"
                  value={addressFormData.adres}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      adres: e.target.value,
                    })
                  }
                  rows="5"
                  maxLength={500}
                  placeholder="Adres bilgilerini buraya girin..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#ffffff',
                    color: '#1e293b',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: '#64748b'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-info-circle" style={{ fontSize: '11px' }}></i>
                    Minimum 5 karakter gerekli
                  </span>
                  <span style={{
                    fontWeight: addressFormData.adres.length > 450 ? 600 : 400,
                    color: addressFormData.adres.length > 450 ? '#f59e0b' : '#64748b'
                  }}>
                    {addressFormData.adres.length}/500 karakter
                  </span>
                </div>
              </div>

              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-city" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  İl *
                </label>
                <select
                  value={addressFormData.cityId}
                  onChange={(e) => {
                    setAddressFormData({
                      ...addressFormData,
                      cityId: e.target.value,
                      districtId: '', // İl değiştiğinde ilçeyi sıfırla
                    });
                    setAddressIlceler([]); // İl değiştiğinde ilçe listesini temizle
                  }}
                  required
                  disabled={addressIllerLoading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#ffffff',
                    color: '#1e293b',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    cursor: addressIllerLoading ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">İl Seçiniz</option>
                  {addressIllerLoading ? (
                    <option disabled>İller yükleniyor...</option>
                  ) : addressIller.length === 0 ? (
                    <option disabled>İl bulunamadı</option>
                  ) : (
                    addressIller.map((il) => (
                      <option key={il.id || il._id} value={String(il.id || il._id)}>
                        {il.name || il.ad}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="form-group" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fas fa-map-pin" style={{ fontSize: '12px', color: '#64748b' }}></i>
                  İlçe *
                </label>
                <select
                  value={addressFormData.districtId}
                  onChange={(e) => {
                    setAddressFormData({
                      ...addressFormData,
                      districtId: e.target.value,
                    });
                  }}
                  required
                  disabled={!addressFormData.cityId || addressIlcelerLoading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#ffffff',
                    color: '#1e293b',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    cursor: (!addressFormData.cityId || addressIlcelerLoading) ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">İlçe Seçiniz</option>
                  {addressIlcelerLoading ? (
                    <option disabled>İlçeler yükleniyor...</option>
                  ) : !addressFormData.cityId ? (
                    <option disabled>Önce il seçiniz</option>
                  ) : addressIlceler.length === 0 ? (
                    <option disabled>İlçe bulunamadı</option>
                  ) : (
                    addressIlceler.map((ilce) => {
                      const ilceId = ilce.id || ilce._id;
                      const ilceName = ilce.adi || ilce.ad || ilce.name || '';
                      return (
                        <option key={ilceId} value={String(ilceId)}>
                          {ilceName}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              <div 
                className="form-group"
                style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <label 
                  className="checkbox-label"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    cursor: addresses.length === 0 && !editingAddressId ? 'not-allowed' : 'pointer',
                    opacity: addresses.length === 0 && !editingAddressId ? 0.6 : 1,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1e293b',
                    userSelect: 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={addressFormData.varsayilan}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        varsayilan: e.target.checked,
                      })
                    }
                    disabled={addresses.length === 0 && !editingAddressId}
                    style={{ 
                      cursor: addresses.length === 0 && !editingAddressId ? 'not-allowed' : 'pointer',
                      width: '18px',
                      height: '18px',
                      accentColor: '#2563eb'
                    }}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-star" style={{ fontSize: '13px', color: '#f59e0b' }}></i>
                    Varsayılan Adres Olarak Ayarla
                  </span>
                </label>
                {addresses.length === 0 && !editingAddressId && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px 12px',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                    fontSize: '13px',
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <i className="fas fa-info-circle" style={{ fontSize: '12px' }}></i>
                    <span>İlk adres otomatik olarak varsayılan yapılacaktır.</span>
                  </div>
                )}
              </div>

              {addressFormError && (
                <div className="form-error" style={{
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  color: '#dc2626',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{addressFormError}</span>
                </div>
              )}

              <div 
                className="modal-footer"
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e2e8f0',
                  marginTop: '8px',
                  paddingTop: '20px'
                }}
              >
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleCloseAddressModal}
                  disabled={addressSubmitting}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#64748b',
                    cursor: addressSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: addressSubmitting ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!addressSubmitting) {
                      e.target.style.backgroundColor = '#f8fafc';
                      e.target.style.borderColor = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!addressSubmitting) {
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  <i className="fas fa-times"></i>
                  İptal
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={addressSubmitting}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: 'none',
                    background: addressSubmitting ? '#94a3b8' : '#2563eb',
                    color: '#ffffff',
                    cursor: addressSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: addressSubmitting ? 'none' : '0 2px 8px rgba(37, 99, 235, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!addressSubmitting) {
                      e.target.style.backgroundColor = '#1d4ed8';
                      e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!addressSubmitting) {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {addressSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ürün Seçimi Modal */}
      {isProductSelectModalOpen && (
        <div
          className="modal-overlay category-select-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCloseProductSelectModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            className="modal-content category-select-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
              width: '100%',
              maxWidth: '920px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div className="modal-header" style={{
              padding: '24px 28px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1e293b',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <i className="fas fa-search" style={{ color: '#2563eb' }}></i>
                Ürün Seçin
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseProductSelectModal}
                aria-label="Kapat"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{
              padding: '24px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              <DeviceProductTreePicker
                selectedId={selectedProductForModal?.id || selectedProductForModal?._id}
                onSelect={handleProductSelect}
              />
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.45 }}>
                Ürünleri servis kaydı oluştururkenki gibi kategori ağacından seçebilirsiniz. Arama kutusu listede eşleşen harfleri vurgular.
              </p>

              <div className="modal-footer" style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e2e8f0',
                padding: '20px 28px',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleCloseProductSelectModal}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.borderColor = '#e2e8f0';
                  }}
                >
                  <i className="fas fa-times"></i>
                  İptal
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleProductConfirm}
                  disabled={!selectedProductForModal}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    border: 'none',
                    background: selectedProductForModal ? '#2563eb' : '#94a3b8',
                    color: '#ffffff',
                    cursor: selectedProductForModal ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: selectedProductForModal ? '0 2px 8px rgba(37, 99, 235, 0.2)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedProductForModal) {
                      e.target.style.backgroundColor = '#1d4ed8';
                      e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedProductForModal) {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <i className="fas fa-check"></i>
                  Seç
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {smsDetayKayit && (
        <SmsNetgsmDetayModal
          sms={smsDetayKayit}
          onClose={() => setSmsDetayKayit(null)}
        />
      )}
    </main>
  );
};

export default CustomerFormPage;



